import { Box, Typography } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const FUNDING_TYPE_ORDER = ["Pre-Seed", "Seed", "Series A", "Series B", "Series C"];
const CLASS_COLORS = ["#4caf50", "#f44336", "#fb8c00"];
const QUARTER_OPTIONS = ["Q1", "Q2", "Q3", "Q4"];
const MONTH_ORDER = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const filterCoverageDefinitions = [
  {
    label: "Industry",
    hasValue: (company) => Array.isArray(company.BucketIndustries) && company.BucketIndustries.length > 0,
  },
  {
    label: "Country",
    hasValue: (company) =>
      typeof company["Headquarters Location"] === "string" &&
      company["Headquarters Location"].trim() !== "" &&
      company["Headquarters Location"] !== "Unknown",
  },
  {
    label: "Investment Stage",
    hasValue: (company) =>
      typeof company["Investment Stage"] === "string" &&
      company["Investment Stage"].trim() !== "" &&
      company["Investment Stage"] !== "Unknown",
  },
  {
    label: "New Company",
    hasValue: (company) =>
      company["Update Category"] === "New Company",
  },
  {
    label: "Updates",
    hasValue: (company) =>
      company["Update Category"] === "Updates",
  },
  {
    label: "Revenue Range",
    hasValue: (company) =>
      typeof company["Estimated Revenue Range"] === "string" &&
      company["Estimated Revenue Range"].trim() !== "",
  },
  {
    label: "Time Period",
    hasValue: (company) =>
      typeof company["Funding Quarter"] === "string" && company["Funding Quarter"].trim() !== "",
  },
  {
    label: "Year",
    hasValue: (company) =>
      typeof company["Funding Year"] === "string" && company["Funding Year"].trim() !== "",
  },
];

const countMap = (arr) =>
  Object.entries(
    arr.reduce((acc, value) => {
      acc[value] = (acc[value] || 0) + 1;
      return acc;
    }, {})
  ).map(([key, count]) => ({ key, count }));

const sortFundingTypeData = (data) =>
  [...data].sort((a, b) => {
    const aIndex = FUNDING_TYPE_ORDER.indexOf(a.key);
    const bIndex = FUNDING_TYPE_ORDER.indexOf(b.key);

    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;

    return a.key.localeCompare(b.key);
  });

const sortDataAlphabetically = (data) =>
  [...data].sort((a, b) => a.key.localeCompare(b.key));

export default function TabMiniCharts({
  companies,
  currentTab,
  showClassificationSummary = false,
  selectedFundingPeriod = [],
}) {
  if (!companies || companies.length === 0) return null;

  const totalCompanies = companies.length;
  const totalLeads = companies.filter((c) => c["Lead Type"] === "ACCEPT").length;
  const totalDiscard = companies.filter((c) => c["Lead Type"] === "NOT ACCEPT").length;
  const totalUnsure = companies.filter((c) => c["Lead Type"] === "FUZZY").length;

  const classificationData = [
    { name: "Leads", value: totalLeads },
    { name: "Discard", value: totalDiscard },
    { name: "Unsure", value: totalUnsure },
  ];

  const industryData = sortDataAlphabetically(
    countMap(companies.flatMap((c) => c.BucketIndustries || []))
  );

  const countryData = sortDataAlphabetically(
    countMap(companies.map((c) => c["Headquarters Location"] || "Unknown"))
  );

  const stageData = sortFundingTypeData(
    countMap(companies.map((c) => c["Investment Stage"] || "Unknown"))
  );

  const fundingQuarterData = QUARTER_OPTIONS
    .map((quarter) => ({
      key: quarter,
      count: companies.filter((c) => c["Funding Quarter"] === quarter).length,
    }))
    .filter((item) => item.count > 0);

  const fundingMonthData = MONTH_ORDER
    .map((month) => ({
      key: month,
      count: companies.filter((c) => c["Funding Month"] === month).length,
    }))
    .filter((item) => item.count > 0);

  const isMonthPeriodSelected =
    selectedFundingPeriod.length > 0 &&
    selectedFundingPeriod.some((period) => MONTH_ORDER.includes(period));

  const fundingTimeData = isMonthPeriodSelected
    ? fundingMonthData
    : fundingQuarterData;

  const tabSummaryLabel =
    currentTab === "ACCEPT"
      ? "Total Leads"
      : currentTab === "NOT ACCEPT"
      ? "Total Discard"
      : currentTab === "FUZZY"
      ? "Total Unsure"
      : "Total Companies";

  const filterCoverageData = filterCoverageDefinitions.map(({ label, hasValue }) => ({
    key: label,
    count: companies.filter((company) => hasValue(company)).length,
  }));

  const renderChart = (
    title,
    data,
    color,
    angle = 0,
    height = 230,
    layout = "horizontal"
  ) => {
    if (!data || data.length === 0) return null;

    return (
      <>
        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
          {title}
        </Typography>

        <ResponsiveContainer width="100%" height={height}>
          <BarChart
            data={data}
            layout={layout}
            margin={layout === "vertical" ? { left: 20, right: 16 } : undefined}
          >
            <CartesianGrid strokeDasharray="3 3" />
            {layout === "vertical" ? (
              <>
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="key" width={110} />
              </>
            ) : (
              <>
                <XAxis
                  dataKey="key"
                  angle={angle}
                  textAnchor={angle === 0 ? "middle" : "end"}
                  height={angle === -90 ? 110 : 50}
                  interval={0}
                />
                <YAxis allowDecimals={false} />
              </>
            )}
            <Tooltip />
            <Legend />
            <Bar dataKey="count" fill={color} />
          </BarChart>
        </ResponsiveContainer>
      </>
    );
  };

  return (
    <Box sx={{ mt: 3, mb: 4 }}>
      {!showClassificationSummary && (
        <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
          {tabSummaryLabel}: {totalCompanies}
        </Typography>
      )}

      {showClassificationSummary && (
        <Box
          sx={{
            px: 2,
            pt: 0,
            pb: 2,
            mb: 1,
            borderRadius: 3,
          }}
        >
          <Typography variant="h6" sx={{ mt: 0, mb: 1 }}>
            Classification Summary
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "220px minmax(0, 1fr)" },
              alignItems: "start",
              gap: 2,
              mb: 1,
            }}
          >
            <Box
              sx={{
                display: "grid",
                justifyItems: "start",
                gap: 0.35,
              }}
            >
              <Typography variant="body1">
                <strong>Total Companies:</strong> {totalCompanies}
              </Typography>
              <Typography variant="body1">
                <strong>Leads:</strong> {totalLeads}
              </Typography>
              <Typography variant="body1">
                <strong>Unsure:</strong> {totalUnsure}
              </Typography>
              <Typography variant="body1">
                <strong>Discard:</strong> {totalDiscard}
              </Typography>
            </Box>

            <Box sx={{ width: "100%", mt: { xs: 1, md: 0.25 }, ml: { md: -3.5 } }}>
              <ResponsiveContainer width="100%" height={248}>
                <PieChart>
                  <Pie
                    data={classificationData}
                    dataKey="value"
                    nameKey="name"
                    cy="44%"
                    outerRadius={98}
                    labelLine={false}
                  >
                    {classificationData.map((entry, index) => (
                      <Cell key={entry.name} fill={CLASS_COLORS[index]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" align="center" />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </Box>
      )}

      {renderChart("Filter Field Coverage", filterCoverageData, "#14b8a6", 0, 320, "vertical")}
      {renderChart("Industry Distribution", industryData, "#1976d2", 0)}
      {renderChart("Country Distribution", countryData, "#4caf50", -90, 340)}
      {renderChart("Funding Distribution Over Time", fundingTimeData, "#0f766e", 0)}
      {renderChart("Funding Type Distribution", stageData, "#9c27b0", 0)}
    </Box>
  );
}
